import type { Mismatch } from '../types'
import { money, relativeTime } from '../lib/format'

const TYPE_STYLES: Record<string, string> = {
  missing_in_omega: 'bg-red-500/20 text-red-300 border-red-500/30',
  missing_in_psp: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  status_mismatch: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  amount_mismatch: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
}

const STATUS_STYLES: Record<string, string> = {
  confirmed: 'bg-red-500/20 text-red-300',
  acknowledged: 'bg-yellow-500/20 text-yellow-300',
  resolved: 'bg-emerald-500/20 text-emerald-300',
  auto_resolved: 'bg-slate-500/20 text-slate-300',
}

interface Props {
  rows: Mismatch[]
  onAck?: (m: Mismatch) => void
  onResolve?: (m: Mismatch) => void
  readOnly?: boolean
  showBrand?: boolean
}

const BRAND_PILL: Record<string, string> = {
  WOWVEGAS: 'bg-indigo-500/20 text-indigo-300',
  ROLLA: 'bg-red-500/20 text-red-300',
}

export function MismatchTable({ rows, onAck, onResolve, readOnly, showBrand }: Props) {
  if (rows.length === 0) {
    return (
      <div className="border border-slate-800 rounded-lg p-8 text-center text-slate-400">
        Nothing here.
      </div>
    )
  }

  return (
    <div className="border border-slate-800 rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
          <tr className="whitespace-nowrap">
            {showBrand && <th className="text-left px-3 py-2">Brand</th>}
            <th className="text-left px-3 py-2">Ref</th>
            <th className="text-left px-3 py-2">Party ID</th>
            <th className="text-left px-3 py-2">Email</th>
            <th className="text-left px-3 py-2">PSP</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-right px-3 py-2">PSP $</th>
            <th className="text-right px-3 py-2">Omega $</th>
            <th className="text-left px-3 py-2">PSP status</th>
            <th className="text-left px-3 py-2">Omega status</th>
            <th className="text-left px-3 py-2">Age</th>
            <th className="text-left px-3 py-2">Request date</th>
            <th className="text-left px-3 py-2">Process date</th>
            <th className="text-left px-3 py-2">State</th>
            {!readOnly && <th className="text-right px-3 py-2">Actions</th>}
            {readOnly && <th className="text-left px-3 py-2">Resolution</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.id} className="border-t border-slate-800/80">
              {showBrand && (
                <td className="px-3 py-2">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs ${
                      BRAND_PILL[m.brand] ?? 'bg-slate-500/20 text-slate-300'
                    }`}
                  >
                    {m.brand}
                  </span>
                </td>
              )}
              <td className="px-3 py-2 font-mono">{m.reference}</td>
              <td className="px-3 py-2 font-mono text-slate-300">{m.party_id ?? '—'}</td>
              <td className="px-3 py-2 text-slate-300 max-w-[220px] truncate" title={m.email ?? ''}>
                {m.email ?? '—'}
              </td>
              <td className="px-3 py-2 uppercase">{m.psp}</td>
              <td className="px-3 py-2">
                <span
                  className={`inline-block border px-2 py-0.5 rounded text-xs ${
                    TYPE_STYLES[m.mismatch_type] ?? ''
                  }`}
                >
                  {m.mismatch_type}
                </span>
              </td>
              <td className="px-3 py-2 text-right font-mono">{money(m.paycom_amount)}</td>
              <td className="px-3 py-2 text-right font-mono">{money(m.omega_amount)}</td>
              <td className="px-3 py-2">{m.paycom_status ?? '—'}</td>
              <td className="px-3 py-2">{m.omega_status ?? '—'}</td>
              <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                {relativeTime(m.first_detected_at)}
              </td>
              <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                {m.request_date ? m.request_date.replace('T',' ').slice(0,16) + ' UTC' : '—'}
              </td>
              <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                {m.process_date ? m.process_date.replace('T',' ').slice(0,16) + ' UTC' : '—'}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                <span
                  className={`inline-block px-2 py-0.5 rounded text-xs ${
                    STATUS_STYLES[m.status] ?? ''
                  }`}
                >
                  {m.status}
                </span>
                {m.status === 'acknowledged' && m.acknowledged_by && (
                  <div className="text-xs text-slate-500 mt-0.5 truncate max-w-[160px]">
                    by {m.acknowledged_by}
                  </div>
                )}
              </td>
              {!readOnly ? (
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <div className="flex gap-1 justify-end">
                    {m.status === 'confirmed' && (
                      <button
                        onClick={() => onAck?.(m)}
                        className="text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-200 hover:bg-yellow-500/30"
                      >
                        Take
                      </button>
                    )}
                    <button
                      onClick={() => onResolve?.(m)}
                      className="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30"
                    >
                      Resolve
                    </button>
                  </div>
                </td>
              ) : (
                <td className="px-3 py-2">
                  {m.resolution_type ?? '—'}
                  {m.resolved_by && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      by {m.resolved_by}
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
